// Keel: base HubApiClient — request infrastructure + zone composition
import { ApiError, AuthError } from './errors'
import { bridgeMethods } from './client-bridge'
import { helmMethods } from './client-helm'
import { wardroomMethods } from './client-wardroom'
import { quarterdeckMethods } from './client-quarterdeck'
import { boatswainMethods } from './client-boatswain'
import { signalsMethods } from './client-signals'
import { galleyMethods } from './client-galley'
import type { FileRecord } from './types'

// Exported for zone client files to type their parameters
export interface RequestHelper {
  get: <T>(path: string) => Promise<T>
  post: <T>(path: string, data?: unknown) => Promise<T>
  put: <T>(path: string, data?: unknown) => Promise<T>
  patch: <T>(path: string, data?: unknown) => Promise<T>
  del: <T>(path: string) => Promise<T>
}

export interface FileUploadHelper {
  upload: (file: File, projectId?: string, context?: string, contextId?: string) => Promise<{ file: FileRecord }>
}

export class HubApiClient {
  private baseUrl: string
  private getToken: () => string | null

  constructor(baseUrl: string, getToken: () => string | null) {
    this.baseUrl = baseUrl
    this.getToken = getToken

    // Build request helper
    const r: RequestHelper = {
      get: <T>(path: string) => this.request<T>('GET', path),
      post: <T>(path: string, data?: unknown) => this.request<T>('POST', path, data),
      put: <T>(path: string, data?: unknown) => this.request<T>('PUT', path, data),
      patch: <T>(path: string, data?: unknown) => this.request<T>('PATCH', path, data),
      del: <T>(path: string) => this.request<T>('DELETE', path),
    }

    // File upload helper (needs raw fetch for FormData)
    const f: FileUploadHelper = {
      upload: async (file: File, projectId?: string, context = 'general', contextId?: string) => {
        const formData = new FormData()
        formData.append('file', file)
        if (projectId) formData.append('project_id', projectId)
        formData.append('context', context)
        if (contextId) formData.append('context_id', contextId)

        const token = this.getToken()
        const headers: Record<string, string> = {}
        if (token) headers['Authorization'] = `Bearer ${token}`

        const response = await fetch(`${this.baseUrl}/hub/files`, {
          method: 'POST',
          headers,
          body: formData,
        })

        if (response.status === 401) throw new AuthError()
        const result = await response.json()
        if (!response.ok) throw new ApiError(result.error || 'Upload failed', response.status)
        return result
      },
    }

    // Compose zone methods
    Object.assign(this, bridgeMethods(r))
    Object.assign(this, helmMethods(r))
    Object.assign(this, wardroomMethods(r))
    Object.assign(this, quarterdeckMethods(r))
    Object.assign(this, boatswainMethods(r))
    Object.assign(this, signalsMethods(r, f))
    Object.assign(this, galleyMethods(r))

    // Public request methods for endpoints without dedicated client methods
    this.fetch = r
  }

  private async request<T>(method: string, path: string, data?: unknown): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    const token = this.getToken()
    if (token) headers['Authorization'] = `Bearer ${token}`

    const options: RequestInit = { method, headers }
    if (data && method !== 'GET') {
      options.body = JSON.stringify(data)
    }

    const response = await fetch(`${this.baseUrl}${path}`, options)

    if (response.status === 401) {
      throw new AuthError()
    }

    const result = await response.json()

    if (!response.ok) {
      throw new ApiError(result.error || 'Request failed', response.status)
    }

    return result as T
  }

  // Public escape hatch for ad-hoc requests
  fetch!: RequestHelper

  // Zone method declarations for TypeScript
  // Bridge
  declare auth: ReturnType<typeof bridgeMethods>['auth']
  declare dashboard: ReturnType<typeof bridgeMethods>['dashboard']
  declare projects: ReturnType<typeof bridgeMethods>['projects']
  declare activity: ReturnType<typeof bridgeMethods>['activity']
  declare users: ReturnType<typeof bridgeMethods>['users']
  declare admiral: ReturnType<typeof bridgeMethods>['admiral']
  declare squadronAdmin: ReturnType<typeof bridgeMethods>['squadronAdmin']
  // Helm
  declare captain: ReturnType<typeof helmMethods>['captain']
  declare market: ReturnType<typeof helmMethods>['market']
  declare competitors: ReturnType<typeof helmMethods>['competitors']
  declare roadmap: ReturnType<typeof helmMethods>['roadmap']
  // Wardroom
  declare surveys: ReturnType<typeof wardroomMethods>['surveys']
  declare whiteboards: ReturnType<typeof wardroomMethods>['whiteboards']
  declare comments: ReturnType<typeof wardroomMethods>['comments']
  declare discussions: ReturnType<typeof wardroomMethods>['discussions']
  declare standups: ReturnType<typeof wardroomMethods>['standups']
  declare wiki: ReturnType<typeof wardroomMethods>['wiki']
  declare reviews: ReturnType<typeof wardroomMethods>['reviews']
  declare polls: ReturnType<typeof wardroomMethods>['polls']
  declare bookmarks: ReturnType<typeof wardroomMethods>['bookmarks']
  declare retros: ReturnType<typeof wardroomMethods>['retros']
  // Quarterdeck
  declare milestones: ReturnType<typeof quarterdeckMethods>['milestones']
  declare okrs: ReturnType<typeof quarterdeckMethods>['okrs']
  declare timeTracking: ReturnType<typeof quarterdeckMethods>['timeTracking']
  declare templates: ReturnType<typeof quarterdeckMethods>['templates']
  // Boatswain
  declare profiles: ReturnType<typeof boatswainMethods>['profiles']
  declare invites: ReturnType<typeof boatswainMethods>['invites']
  // Signals
  declare notifications: ReturnType<typeof signalsMethods>['notifications']
  declare notificationPrefs: ReturnType<typeof signalsMethods>['notificationPrefs']
  declare documentAI: ReturnType<typeof signalsMethods>['documentAI']
  declare files: ReturnType<typeof signalsMethods>['files']
  // Galley
  declare restaurants: ReturnType<typeof galleyMethods>['restaurants']
  declare orders: ReturnType<typeof galleyMethods>['orders']
  declare deliveryWindows: ReturnType<typeof galleyMethods>['deliveryWindows']
  declare drivers: ReturnType<typeof galleyMethods>['drivers']
  declare galleyRoutes: ReturnType<typeof galleyMethods>['galleyRoutes']
  declare galleyCustomers: ReturnType<typeof galleyMethods>['galleyCustomers']
  declare galleyAnalytics: ReturnType<typeof galleyMethods>['galleyAnalytics']
}
